import { Button, Grid, MenuItem, TextField, Typography } from "@mui/material";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { useEditExpiryDateCard } from "./hook/useEditExpiryDateCard";
import { Controller } from "react-hook-form";
import { MONTH_LIST } from "@constants";
import { Loader } from "@common/Loader/Loader";

interface IParams {
    accountId: string;
    cardId: number;
    finishEdit(): void;
    setExpiryDate(expiryDate: string): void;
}

export const ExpiryDateCardForm = (props: IParams) => {
    const { cardId, finishEdit, setExpiryDate, accountId } = props;

    const { formMethods, editExpiryDate, editExpiryState } = useEditExpiryDateCard(
        accountId,
        cardId,
        finishEdit,
        setExpiryDate
    );
    const { errors, control } = formMethods;
    const { t } = useTranslation(["common", "glossary", "validation"]);

    const yearsMenuItems = () => {
        const firstYear = new Date().getFullYear();
        const menuItems = [];
        for (let i = 0; i <= 10; ++i) {
            menuItems.push(
                <MenuItem key={firstYear + i} value={firstYear + i}>
                    {firstYear + i}
                </MenuItem>
            );
        }

        return menuItems;
    };

    const monthsMenuItems = () =>
        MONTH_LIST.map((month) => (
            <MenuItem key={month.value} value={month.value}>
                {month.label}
            </MenuItem>
        ));

    return (
        <form className="payment-card__expiry" onSubmit={editExpiryDate}>
            <Grid alignItems="center" container justifyContent="center" spacing={2}>
                <Grid item md={5} xs={6}>
                    <Controller
                        control={control}
                        name="expiryMonthField"
                        render={(renderProps) => (
                            <TextField
                                disabled={editExpiryState.isLoading}
                                error={Boolean(errors.expiryMonthField)}
                                fullWidth
                                helperText={errors.expiryMonthField?.message}
                                id="expiry-month"
                                inputRef={renderProps.ref}
                                label={t("glossary:month")}
                                name={renderProps.name}
                                select
                                value={renderProps.value}
                                variant="standard"
                                onChange={renderProps.onChange}
                            >
                                {monthsMenuItems()}
                            </TextField>
                        )}
                        rules={{
                            required: t("validation:required"),
                        }}
                    />
                </Grid>
                <Grid item md={5} xs={6}>
                    <Controller
                        control={control}
                        name="expiryYearField"
                        render={(renderProps) => (
                            <TextField
                                disabled={editExpiryState.isLoading}
                                error={Boolean(errors.expiryYearField)}
                                fullWidth
                                helperText={errors.expiryYearField?.message}
                                id="expiry-year"
                                inputRef={renderProps.ref}
                                label={t("glossary:year")}
                                name={renderProps.name}
                                select
                                value={renderProps.value}
                                variant="standard"
                                onChange={renderProps.onChange}
                            >
                                {yearsMenuItems()}
                            </TextField>
                        )}
                        rules={{
                            required: t("validation:required"),
                        }}
                    />
                </Grid>
            </Grid>
            <Grid container justifyContent="center" spacing={2}>
                {!editExpiryState.isLoading && (
                    <Fragment>
                        <Grid item xs={5}>
                            <Button color="secondary" fullWidth variant="outlined" onClick={finishEdit}>
                                {t("common:cancel")}
                            </Button>
                        </Grid>
                        <Grid item xs={5}>
                            <Button color="secondary" fullWidth type="submit" variant="contained">
                                {t("common:confirm")}
                            </Button>
                        </Grid>
                    </Fragment>
                )}
                {editExpiryState.isLoading && <Loader size={40} />}
                {editExpiryState.error && <Typography color="error">{editExpiryState.error.errorMessage}</Typography>}
            </Grid>
        </form>
    );
};
